import { FC } from 'react';
import cx from 'classnames';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import DotRange, { DotRangeLegend } from '../../DotRange/DotRange';
import {
  BidRank,
  BidSpread,
  PurchaseBid,
  amountText,
  amountWithUnit,
  percentFromAverage,
  rankOf,
  spreadOf,
} from './bidsOfPurchase';
import styles from './PurchaseBids.module.scss';

const signedPercent = (percent: number) => `${percent < 0 ? '−' : percent > 0 ? '+' : ''}${Math.abs(percent)} %`;

type TooltipProps = {
  bid: PurchaseBid;
  spread: BidSpread;
  rank: BidRank;
  isOnlyBid: boolean;
};

const BidTooltip: FC<TooltipProps> = ({ bid, spread, rank, isOnlyBid }) => {
  const [t] = useTranslation('projects', { keyPrefix: 'purchaseList.bids' });
  const percent = percentFromAverage(bid.amount, spread.average);

  const rankLabel = () => {
    if (rank.kind === 'lowest') return t('lowest', 'Lägst');
    if (rank.kind === 'highest') return t('highest', 'Högst');
    return t('rank', '{{position}} av {{count}}', { position: rank.position, count: rank.count });
  };

  return (
    <div className={styles.tooltip}>
      <div className={styles.tooltipHeader}>
        <span className={styles.supplierName}>{bid.supplierName}</span>
        {!isOnlyBid && <span className={styles.rank}>{rankLabel()}</span>}
      </div>
      <div className={styles.amount}>{amountWithUnit(bid.amount, spread.unit)}</div>
      {!isOnlyBid && (
        <div className={styles.difference}>
          <span className={cx(styles.percent, { [styles.below]: percent < 0, [styles.above]: percent > 0 })}>
            {signedPercent(percent)}
          </span>
          {t('comparedToAverage', 'mot uppskattad summa')}
        </div>
      )}
      {bid.bidDate && (
        <div className={styles.received}>
          {t('received', 'Inkom {{date}}', { date: moment(bid.bidDate).format('D MMM YYYY') })}
        </div>
      )}
    </div>
  );
};

type ChartProps = {
  bids: PurchaseBid[];
  spread: BidSpread;
};

const BidChart: FC<ChartProps> = ({ bids, spread }) => {
  const [t] = useTranslation('projects', { keyPrefix: 'purchaseList.bids' });
  const isOnlyBid = bids.length === 1;
  const points = bids.map(bid => ({
    id: bid.supplierId,
    value: bid.amount,
    tooltip: <BidTooltip bid={bid} spread={spread} rank={rankOf(bid.amount, bids)} isOnlyBid={isOnlyBid} />,
  }));

  return (
    <div className={styles.chart}>
      <div className={styles.headline}>
        {isOnlyBid ? t('onlyBid', 'Enda anbud') : t('average', 'Snitt')}{' '}
        <strong>{amountWithUnit(spread.average, spread.unit)}</strong>
      </div>
      <DotRange points={points} marker={isOnlyBid ? undefined : spread.average} />
      {!isOnlyBid && (
        <div className={styles.extremes}>
          <span>{amountText(spread.lowest)}</span>
          <span>{amountText(spread.highest)}</span>
        </div>
      )}
    </div>
  );
};

type Props = {
  bids: PurchaseBid[];
};

export const PurchaseBids: FC<Props> = ({ bids }) => {
  if (!bids.length) return null;

  const spread = spreadOf(bids);
  return (
    <div className={styles.container}>
      <span className={styles.count}>{bids.length}</span>
      {spread && <BidChart bids={bids} spread={spread} />}
    </div>
  );
};

export const PurchaseBidsTitle: FC = () => {
  const [t] = useTranslation('projects', { keyPrefix: 'purchaseList' });
  return (
    <span className={styles.title}>
      {t('columns.bids', 'Anbud')}
      <DotRangeLegend
        pointLabel={t('bids.legend.bid', 'anbud')}
        spanLabel={t('bids.legend.spread', 'spann')}
        markerLabel={t('bids.legend.average', 'uppskattad summa')}
      />
    </span>
  );
};
